import { escapeHtml } from '../util/escapeHtml.js';
import { anchored } from '../util/viewportAnchor.js';
import { mountShell } from '../components/Shell.js';
import { api, HttpError } from '../api/client.js';
import { store } from '../state/store.js';
import type {
  AwardDto,
  BoonShopEntryDto,
  NewlyGrantedAwardDto,
} from '@rex-mortis/shared';

interface FlashState {
  kind: 'success' | 'danger' | 'warn';
  message: string;
}

type Tab = 'awards' | 'boons';

export async function renderAwards(root: HTMLElement): Promise<void> {
  let awards: AwardDto[] = [];
  let newlyGranted: NewlyGrantedAwardDto[] = [];
  let boons: BoonShopEntryDto[] = [];
  let boonBalance = 0;
  let flash: FlashState | null = null;
  let activeTab: Tab = 'awards';

  async function refresh(): Promise<void> {
    try {
      const me = await api.me();
      store.set({ account: me.account, character: me.character });
      const a = await api.listAwards();
      awards = a.awards;
      newlyGranted = a.newlyGranted;
      const b = await api.listBoons();
      boons = b.boons;
      boonBalance = b.balance;
    } catch (err) {
      const msg = err instanceof HttpError ? err.message : 'Failed to load.';
      flash = { kind: 'danger', message: msg };
    }
  }

  await refresh();

  // If awards were granted on read, surface a celebratory flash once.
  if (newlyGranted.length > 0 && !flash) {
    const total = newlyGranted.reduce((s, n) => s + n.boonReward, 0);
    flash = {
      kind: 'success',
      message: `New awards: ${newlyGranted
        .map((n) => n.name)
        .join(', ')} (+${total} Boons)`,
    };
  }

  mountShell(root, (main) => {
    const renderMain = () => {
      const char = store.get().character;
      if (!char) {
        main.innerHTML = '<p>Loading…</p>';
        return;
      }

      main.innerHTML = `
        <img class="page-banner" src="/img/banners/awards.webp" alt="" />
        <h1>Awards</h1>
        <p style="font-style:italic; color: var(--c-ink-soft); margin-top: -8px;">
          Deeds remembered. Boons unlocked.
        </p>

        <div style="display:flex; gap: var(--s-2); margin-top: var(--s-3); flex-wrap:wrap;">
          <button class="btn ${activeTab === 'awards' ? '' : 'ghost'}" data-tab="awards">Achievements</button>
          <button class="btn ${activeTab === 'boons' ? '' : 'ghost'}" data-tab="boons">Boon Shop (${char.boons} Boons)</button>
        </div>

        <div id="flash" style="margin-top: var(--s-3);"></div>

        <div id="content" style="margin-top: var(--s-3);"></div>
      `;

      const style = document.createElement('style');
      style.textContent = `
        .award-card, .boon-card {
          background: var(--c-parchment);
          border: 1px solid var(--c-rule);
          border-radius: var(--radius);
          padding: var(--s-3);
          display: flex; flex-direction: column;
        }
        .award-card.achieved { border-color: var(--c-gold); background: var(--c-parchment-dark); }
        .award-card.locked { opacity: 0.55; }
        .award-grid, .boon-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
          gap: var(--s-3);
          margin-top: var(--s-3);
        }
        .award-cat-heading {
          font-family: var(--font-display);
          color: var(--c-oxblood);
          border-bottom: 1px solid var(--c-rule);
          padding-bottom: var(--s-1);
          margin: var(--s-5) 0 var(--s-2) 0;
        }
        .pill {
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule-soft);
          border-radius: 999px;
          padding: 1px 8px;
          font-family: var(--font-numeric);
          font-size: 0.85rem;
        }
        /* (gold pill styling lives in pages.css — don't override here) */
        .pill.success { border-color: var(--c-success); color: var(--c-success); }
        .ranks-bar {
          display: flex; gap: 3px;
          margin: var(--s-2) 0;
        }
        .rank-pip {
          flex: 1;
          height: 8px;
          background: var(--c-parchment-dark);
          border: 1px solid var(--c-rule-soft);
          border-radius: 2px;
        }
        .rank-pip.filled { background: var(--c-gold); border-color: var(--c-gold); }
      `;
      main.appendChild(style);

      // Tab switching
      main.querySelectorAll<HTMLButtonElement>('button[data-tab]').forEach((btn) => {
        btn.addEventListener('click', () => {
          activeTab = btn.dataset.tab as Tab;
          anchored(renderMain);
        });
      });

      const flashRoot = main.querySelector<HTMLDivElement>('#flash')!;
      if (flash) {
        const div = document.createElement('div');
        div.className = `alert ${flash.kind === 'warn' ? 'danger' : flash.kind}`;
        div.textContent = flash.message;
        flashRoot.appendChild(div);
      }

      const content = main.querySelector<HTMLDivElement>('#content')!;
      if (activeTab === 'awards') {
        renderAwardsTab(content, awards);
      } else {
        renderBoonsTab(content, boons, boonBalance, refresh, (f) => (flash = f), renderMain);
      }
    };

    anchored(renderMain);
  });
}

// ──────────────────────────────────────────────────────────────────────────────
// Tabs
// ──────────────────────────────────────────────────────────────────────────────

function renderAwardsTab(content: HTMLDivElement, awards: AwardDto[]): void {
  // Group by category
  const byCategory = new Map<string, AwardDto[]>();
  for (const a of awards) {
    const list = byCategory.get(a.category) ?? [];
    list.push(a);
    byCategory.set(a.category, list);
  }

  const categoryOrder = [
    'progression',
    'crime',
    'combat',
    'crypt',
    'economy',
    'exploration',
    'social',
  ];
  const sortedCats = [...byCategory.keys()].sort(
    (a, b) => (categoryOrder.indexOf(a) - categoryOrder.indexOf(b)) || a.localeCompare(b),
  );

  const totalAchieved = awards.filter((a) => a.achieved).length;
  const totalBoonsEarned = awards
    .filter((a) => a.achieved)
    .reduce((s, a) => s + a.boonReward, 0);

  let html = `
    <section class="panel">
      <div style="display:flex; flex-wrap:wrap; gap: var(--s-3);">
        <span class="pill">Achieved: <strong>${totalAchieved}</strong> / ${awards.length}</span>
        <span class="pill gold">Boons earned all-time: <strong>${totalBoonsEarned}</strong></span>
      </div>
    </section>
  `;

  for (const cat of sortedCats) {
    const list = byCategory.get(cat)!;
    list.sort((a, b) => a.name.localeCompare(b.name));
    html += `
      <h2 class="award-cat-heading">${capitalize(cat)}</h2>
      <div class="award-grid">
        ${list
          .map(
            (a) => `
          <div class="award-card ${a.achieved ? 'achieved' : 'locked'}">
            <h3 style="margin: 0 0 4px 0;">${escapeHtml(a.name)}</h3>
            <p style="font-style:italic; color: var(--c-ink-soft); font-size: 0.92rem; flex: 1; margin: 0 0 var(--s-2) 0;">${escapeHtml(a.blurb)}</p>
            <div style="display:flex; flex-wrap:wrap; gap: 4px; font-size: 0.85rem;">
              <span class="pill ${a.achieved ? 'success' : ''}">${a.achieved ? '✓ Achieved' : 'Locked'}</span>
              <span class="pill gold">+${a.boonReward} Boons</span>
              ${a.achievedAt ? `<span class="pill">on ${new Date(a.achievedAt).toLocaleDateString()}</span>` : ''}
            </div>
          </div>
        `,
          )
          .join('')}
      </div>
    `;
  }

  content.innerHTML = html;
}

function renderBoonsTab(
  content: HTMLDivElement,
  boons: BoonShopEntryDto[],
  balance: number,
  refresh: () => Promise<void>,
  setFlash: (f: FlashState) => void,
  rerender: () => void,
): void {
  content.innerHTML = `
    <section class="panel">
      <h2 style="margin-top:0;">Boon Shop</h2>
      <p style="font-style:italic; color: var(--c-ink-soft);">
        Spend Boons earned from Awards on permanent stat-cap upgrades. Each rank stacks.
      </p>
      <div style="display:flex; flex-wrap:wrap; gap: var(--s-3); margin-top: var(--s-2);">
        <span class="pill gold">Balance: <strong>${balance}</strong> Boons</span>
      </div>
    </section>

    <div class="boon-grid">
      ${boons
        .map((b) => {
          // Uncapped boons never max out and have no fixed pip count.
          const maxed = !b.uncapped && b.ownedRanks >= b.maxRanks;
          const canAfford = b.nextCost !== null && balance >= b.nextCost;
          const pips = b.uncapped
            ? ''
            : Array.from({ length: b.maxRanks })
                .map(
                  (_, i) =>
                    `<div class="rank-pip ${i < b.ownedRanks ? 'filled' : ''}"></div>`,
                )
                .join('');
          const ranksLabel = b.uncapped
            ? `${b.ownedRanks} ranks · ∞`
            : `${b.ownedRanks} / ${b.maxRanks} ranks`;
          return `
        <div class="boon-card">
          <div style="display:flex; align-items:center; gap: 10px; margin-bottom: var(--s-2);">
            <img src="/img/boons/${b.slug}.webp" alt="" width="56" height="56" />
            <h3 style="margin: 0;">${escapeHtml(b.name)}</h3>
          </div>
          <p style="font-style:italic; color: var(--c-ink-soft); font-size: 0.92rem; flex: 1; margin: 0 0 var(--s-2) 0;">${escapeHtml(b.blurb)}</p>
          ${b.uncapped ? '' : `<div class="ranks-bar">${pips}</div>`}
          <div style="display:flex; flex-wrap:wrap; gap: 4px; margin-bottom: var(--s-2); font-size: 0.85rem;">
            <span class="pill">${ranksLabel}</span>
            ${maxed ? '<span class="pill success">MAXED</span>' : `<span class="pill gold">next: ${b.nextCost} Boons</span>`}
          </div>
          <button class="btn" data-buy="${b.slug}" ${maxed || !canAfford ? 'disabled' : ''}>
            ${maxed ? 'Maxed' : !canAfford ? 'Not enough Boons' : `Buy rank ${b.ownedRanks + 1}`}
          </button>
        </div>
      `;
        })
        .join('')}
    </div>
  `;

  content.querySelectorAll<HTMLButtonElement>('button[data-buy]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const slug = btn.dataset.buy!;
      btn.disabled = true;
      try {
        const r = await api.buyBoon({ boonSlug: slug });
        store.set({ character: r.character });
        setFlash({
          kind: 'success',
          message: `${r.boonName} → Rank ${r.newRank}. Spent ${r.costPaid} Boons.`,
        });
        await refresh();
      } catch (err) {
        const msg = err instanceof HttpError ? err.message : 'Purchase failed.';
        setFlash({ kind: 'danger', message: msg });
      } finally {
        rerender();
      }
    });
  });
}

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

